import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Home, RefreshCw, Edit2, Save, X, Settings, LogOut, Plus, Trash2, Image as ImageIcon, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import MenuCard from '../components/MenuCard';
import MenuLineItem from '../components/MenuLineItem';
import ImageUploader from '../components/ImageUploader';
import ImageLightbox from '../components/ImageLightbox';
import OptimizedImage from '../components/OptimizedImage';
import { MenuStyleRenderer } from '../components/menu/MenuStyleRenderer';
import { MENU_STYLES, DEFAULT_CATEGORY_STYLES } from '../components/menu/index';
import { menuItems as mockMenuItems } from '../mockData';
import { getPublicMenuItems, verifyAdminToken, updateMenuItem, createMenuItem, deleteMenuItem, getPageContent, getDailySpecials, getMenuCategoryStyles, getLocations } from '../services/api';
import { toast } from '../hooks/use-toast';
import { safeHtml } from '../utils/sanitize';
// Extracted components for menu page
import { HookahSection } from '../components/menu-page';

const MenuPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  const [pageContent, setPageContent] = useState({});
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [dailySpecialsMap, setDailySpecialsMap] = useState({});
  const [categoryStyles, setCategoryStyles] = useState({});
  
  // Admin editing state
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    category: 'starters',
    image: '',
    is_available: true
  });
  
  // Lightbox state
  const [lightboxItem, setLightboxItem] = useState(null);

  // Location state
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [findingLocation, setFindingLocation] = useState(true);

  // Category display names
  const categoryNames = {
    'daily-specials': '$5 Daily Specials',
    'starters': 'Starters',
    'sides': 'Sides',
    'entrees': 'Entrees',
    'seafood-grits': 'Seafood & Grits',
    'sandwiches': 'Sandwiches',
    'salads': 'Salads',
    'beer-wine': 'Beer & Wine',
    'cocktails': 'Signature Cocktails',
    'signature-cocktails': 'Signature Cocktails',
    'mocktails': 'Handcrafted Mocktails',
    'sodas-spritzers': 'Sodas & Spritzers',
    'teas-lemonades': 'Teas & Lemonades',
    'chilled-juices': 'Chilled Juices',
    'custom-lemonades': 'Custom Fruit Lemonades',
    'hookah': 'Hookah',
    'brunch': 'Brunch',
    'brunch-drinks': 'Brunch Drinks',
    'brunch-sides': 'Brunch Sides'
  };

  // Main categories (6 buttons)
  const mainCategories = [
    { id: 'all', name: 'All', icon: '✨' },
    { id: 'daily-specials', name: '$5 Daily Specials', icon: '⭐' },
    { id: 'food', name: 'Food', icon: '🍽️' },
    { id: 'drinks', name: 'Drinks', icon: '🍹' },
    { id: 'hookah', name: 'Hookah', icon: '💨' }
  ];

  // Food sub-categories
  const foodSubCategories = [
    { id: 'starters', name: 'Starters', icon: '🍤' },
    { id: 'sides', name: 'Sides', icon: '🍟' },
    { id: 'entrees', name: 'Entrees', icon: '🍖' },
    { id: 'seafood-grits', name: 'Seafood & Grits', icon: '🦞' },
    { id: 'sandwiches', name: 'Sandwiches', icon: '🥪' },
    { id: 'salads', name: 'Salads', icon: '🥗' },
    { id: 'brunch', name: 'Brunch', icon: '🥞' },
    { id: 'brunch-sides', name: 'Brunch Sides', icon: '🥓' }
  ];

  // Drinks sub-categories (Cocktails first, then Beer & Wine, Non-Alcoholic)
  const drinksSubCategories = [
    { id: 'cocktails', name: 'Cocktails', icon: '🍸' },
    { id: 'beer-wine', name: 'Beer & Wine', icon: '🍺' },
    { id: 'non-alcoholic', name: 'Non-Alcoholic', icon: '🥤' }
  ];

  // Cocktails sub-categories (nested under Drinks > Cocktails)
  const cocktailSubCategories = [
    { id: 'signature-cocktails', name: 'Signature Cocktails', icon: '🍸' },
    { id: 'brunch-drinks', name: 'Brunch Drinks', icon: '🥂' }
  ];

  // Non-Alcoholic sub-categories (nested under Drinks > Non-Alcoholic)
  const nonAlcoholicSubCategories = [
    { id: 'mocktails', name: 'Handcrafted Mocktails', icon: '🍹' },
    { id: 'sodas-spritzers', name: 'Sodas & Spritzers', icon: '🥤' },
    { id: 'teas-lemonades', name: 'Teas & Lemonades', icon: '🍵' },
    { id: 'chilled-juices', name: 'Chilled Juices', icon: '🧃' },
    { id: 'custom-lemonades', name: 'Custom Fruit Lemonades', icon: '🍋' }
  ];

  // All food category IDs
  const foodCategoryIds = ['starters', 'sides', 'entrees', 'seafood-grits', 'sandwiches', 'salads', 'brunch', 'brunch-sides'];
  
  // All cocktail category IDs
  const cocktailCategoryIds = ['cocktails', 'signature-cocktails', 'brunch-drinks'];
  const nonAlcoholicCategoryIds = ['mocktails', 'sodas-spritzers', 'teas-lemonades', 'chilled-juices', 'custom-lemonades'];
  const beerWineCategoryIds = ['beer-wine'];
  const drinksCategoryIds = [...beerWineCategoryIds, ...cocktailCategoryIds, ...nonAlcoholicCategoryIds];

  // Daily Specials by day of week
  const DEFAULT_DAILY_SPECIALS = {
    0: { // Sunday
      name: "The Sunday Slide",
      description: "Close out the weekend with $5 Daily Specials",
      hours: "6pm – Close",
      emoji: "🌅"
    },
    1: { // Monday
      name: "Margarita Monday",
      description: "Sip on $5 Margaritas all day long",
      hours: "12pm – 8pm",
      emoji: "🍹"
    },
    2: { // Tuesday
      name: "Tito's, Tacos & Tequila",
      description: "$5 Tito's, $5 Margaritas, and $5 Tacos. The perfect Tuesday trio",
      hours: "12pm – 8pm",
      emoji: "🌮"
    },
    3: { // Wednesday
      name: "Wine Down (or Whiskey Up)",
      description: "$5 Select wine glasses, $10 Bottles, or $5 Select Whiskey shots",
      hours: "12pm – 8pm",
      emoji: "🍷"
    },
    4: { // Thursday
      name: "Martini Madness",
      description: "Elevate your Thursday with $5 Martinis. Strawberry, Peach, Mango, Watermelon, Lemon Drop, Melon, Blue (+$1)",
      hours: "12pm – 8pm",
      emoji: "🍸"
    },
    5: { // Friday
      name: "Premium Power Hour",
      description: "$5 Daily Specials menu 12pm – 8pm · $6 Premium Shots 6pm – 8pm",
      hours: "12pm – 8pm",
      emoji: "⚡"
    },
    6: { // Saturday
      name: "Saturday Prime",
      description: "Special $5 menu available",
      hours: "5pm – 8pm",
      emoji: "🌟"
    }
  };

  const buildDailySpecialsMap = (specialsList) => {
    const map = { ...DEFAULT_DAILY_SPECIALS };
    (specialsList || []).forEach((special) => {
      if (typeof special.day_index === 'number') {
        map[special.day_index] = {
          name: special.name,
          description: special.description,
          hours: special.hours,
          emoji: special.emoji
        };
      }
    });
    return map;
  };

  // Get today's special
  const getTodaysSpecial = () => {
    const today = new Date().getDay();
    const map = Object.keys(dailySpecialsMap).length > 0 ? dailySpecialsMap : DEFAULT_DAILY_SPECIALS;
    return map[today] || DEFAULT_DAILY_SPECIALS[today];
  };

  const todaysSpecial = getTodaysSpecial();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const todayName = dayNames[new Date().getDay()];

  // Parse a single hour token like "12pm", "6pm", "11:30pm", "Close" into minutes-from-midnight.
  // "Close" returns 4 * 60 + 24 * 60 = 1680 (i.e. 4:00 AM next day) so a "6pm – Close" window
  // stays active until early morning hours.
  const parseHourToken = (token) => {
    if (!token) return null;
    const t = String(token).trim().toLowerCase().replace(/\s+/g, '');
    if (t === 'close') return 24 * 60 + 4 * 60; // 4:00 AM next day
    const m = t.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const mm = m[2] ? parseInt(m[2], 10) : 0;
    const ampm = m[3];
    if (ampm === 'am') {
      if (h === 12) h = 0;
    } else if (h !== 12) {
      h += 12;
    }
    return h * 60 + mm;
  };

  // Check whether the current local clock falls inside the day's special window.
  // Returns true/false. Accepts hour strings like "12pm – 8pm", "6pm – Close", "5pm-8pm".
  const isSpecialActiveNow = (hoursStr) => {
    if (!hoursStr) return false;
    const parts = String(hoursStr).split(/\s*[-–]\s*/);
    if (parts.length !== 2) return false;
    const startMin = parseHourToken(parts[0]);
    const endMin = parseHourToken(parts[1]);
    if (startMin == null || endMin == null) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    // Same-day window
    if (endMin <= 24 * 60) {
      return nowMin >= startMin && nowMin < endMin;
    }
    // Window wraps past midnight (e.g. 6pm – Close => 6pm Sun to 4am Mon).
    // Active if we're past start today OR before the wrapped end early next morning.
    return nowMin >= startMin || nowMin < (endMin - 24 * 60);
  };

  const specialActiveNow = isSpecialActiveNow(todaysSpecial?.hours);

  // Compute the next time a $5 daily special starts, by walking forward day-by-day.
  // Returns { special, startDateTime, dayIdx } or null.
  const getNextSpecialInfo = useCallback((now = new Date()) => {
    const map = Object.keys(dailySpecialsMap).length > 0 ? dailySpecialsMap : DEFAULT_DAILY_SPECIALS;
    for (let offset = 0; offset < 8; offset++) {
      const probe = new Date(now);
      probe.setDate(probe.getDate() + offset);
      const dayIdx = probe.getDay();
      const spec = map[dayIdx];
      if (!spec || !spec.hours) continue;
      const parts = String(spec.hours).split(/\s*[-–]\s*/);
      if (parts.length !== 2) continue;
      const startMin = parseHourToken(parts[0]);
      if (startMin == null) continue;
      const startDt = new Date(probe);
      startDt.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
      if (startDt > now) return { special: spec, startDateTime: startDt, dayIdx };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailySpecialsMap]);

  // Live ticker — re-renders every 30s so the countdown stays current.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // Build the "starts in …" label for the upcoming special chip.
  const nextSpecialChip = useMemo(() => {
    if (specialActiveNow) return null;
    const now = new Date(nowTick);
    const info = getNextSpecialInfo(now);
    if (!info) return null;
    const diffMs = info.startDateTime - now;
    const diffMin = Math.max(0, Math.round(diffMs / 60000));
    let when;
    if (diffMin < 60) {
      when = diffMin <= 1 ? 'in 1 min' : `in ${diffMin} min`;
    } else if (diffMin < 24 * 60) {
      const h = Math.floor(diffMin / 60);
      const m = diffMin % 60;
      when = m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
    } else {
      const dayLabel = dayNames[info.dayIdx];
      const t = info.startDateTime;
      const hour12 = t.getHours() % 12 || 12;
      const ampm = t.getHours() >= 12 ? 'PM' : 'AM';
      const minStr = t.getMinutes() ? `:${String(t.getMinutes()).padStart(2, '0')}` : '';
      // "starts Sunday 6 PM" / "starts Tomorrow 12 PM"
      const isTomorrow = info.startDateTime.toDateString() ===
        new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toDateString();
      when = `${isTomorrow ? 'Tomorrow' : dayLabel} ${hour12}${minStr} ${ampm}`;
    }
    return {
      label: `$5 ${info.special.name} • ${when.startsWith('in ') ? `starts ${when}` : `starts ${when}`}`,
      emoji: info.special.emoji || '⭐',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialActiveNow, nowTick, getNextSpecialInfo]);

  // If the user is sitting on the Daily Specials view when the window expires,
  // bounce them back to the full menu so they don't see an empty section.
  useEffect(() => {
    if (!specialActiveNow && activeCategory === 'daily-specials') {
      setActiveCategory('all');
    }
  }, [specialActiveNow, activeCategory]);

  const heroHtml = pageContent.hero || 'ELEVATED DINING MEETS SOUTHERN SOUL. EVERY DISH CRAFTED WITH FRESH INGREDIENTS AND GENUINE HOSPITALITY.';


  const renderLineItems = (items) => (
    <div className="space-y-3">
      {items.map((item) => (
        <MenuLineItem key={item.id} item={item} isExpanded={expandedItemId === item.id} onToggleExpand={handleToggleExpand} />
      ))}
    </div>
  );

  const renderClassicRefreshments = () => {
    const sodas = itemsByCategory['sodas-spritzers'] || [];
    const teas = itemsByCategory['teas-lemonades'] || [];
    const juices = itemsByCategory['chilled-juices'] || [];

    if (sodas.length === 0 && teas.length === 0 && juices.length === 0) return null;

    return (
      <div className="mb-10">
        <h3 className="text-2xl font-bold text-white mb-5 border-b border-slate-700 pb-3">Classic Refreshments ($3)</h3>
        {sodas.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-slate-300 mb-3">Sodas & Spritzers</h4>
            {renderLineItems(sodas)}
          </div>
        )}
        {teas.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-slate-300 mb-3">Teas & Lemonades</h4>
            {renderLineItems(teas)}
          </div>
        )}
        {juices.length > 0 && (
          <div>
            <h4 className="text-lg font-semibold text-slate-300 mb-3">Chilled Juices</h4>
            {renderLineItems(juices)}
          </div>
        )}
      </div>
    );
  };

  const handleToggleExpand = (item) => {
    setExpandedItemId((prev) => (prev === item.id ? null : item.id));
  };

  // Fetch menu items from API on mount
  // Geolocation: find closest location, then load menu
  useEffect(() => {
    const init = async () => {
      const checkAdmin = async () => {
        const isValid = await verifyAdminToken();
        setIsAdmin(isValid);
      };
      checkAdmin();
      fetchPageContent();
      fetchDailySpecials();
      fetchCategoryStyles();

      // Load locations and find closest
      try {
        const locs = await getLocations();
        const nonHibachi = (locs || []).filter(l => l.slug !== 'hibachi-food-truck');
        setLocations(nonHibachi);

        // Check saved location first
        const saved = localStorage.getItem('ff_user_location');
        if (saved && nonHibachi.find(l => l.slug === saved)) {
          setSelectedLocation(nonHibachi.find(l => l.slug === saved));
          setFindingLocation(false);
          return;
        }

        // Use geolocation
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              let closest = nonHibachi[0];
              let minDist = Infinity;
              nonHibachi.forEach(loc => {
                if (loc.latitude && loc.longitude) {
                  const d = Math.sqrt(
                    Math.pow(latitude - loc.latitude, 2) +
                    Math.pow(longitude - loc.longitude, 2)
                  );
                  if (d < minDist) { minDist = d; closest = loc; }
                }
              });
              setSelectedLocation(closest);
              setFindingLocation(false);
            },
            () => {
              setSelectedLocation(nonHibachi[0]);
              setFindingLocation(false);
            },
            { timeout: 5000 }
          );
        } else {
          setSelectedLocation(nonHibachi[0]);
          setFindingLocation(false);
        }
      } catch {
        setFindingLocation(false);
      }
    };
    init();
  }, []);

  // Reload menu when location changes
  useEffect(() => {
    if (selectedLocation) {
      fetchMenuItems(selectedLocation.slug);
    }
  }, [selectedLocation]);

  const fetchCategoryStyles = async () => {
    try {
      const dbStyles = await getMenuCategoryStyles();
      // Merge: DB styles override defaults, ensuring all categories are covered
      const merged = { ...DEFAULT_CATEGORY_STYLES, ...(dbStyles || {}) };
      setCategoryStyles(merged);
    } catch (error) {
      console.error('Failed to fetch category styles', error);
      setCategoryStyles({ ...DEFAULT_CATEGORY_STYLES });
    }
  };

  const fetchMenuItems = async (locationSlug) => {
    setLoading(true);
    try {
      const items = await getPublicMenuItems(locationSlug);
      if (items && items.length > 0) {
        setMenuItems(items);
        setUsingMockData(false);
      } else {
        setMenuItems(mockMenuItems);
        setUsingMockData(true);
      }
    } catch (error) {
      console.error('Failed to fetch menu items, using mock data:', error);
      setMenuItems(mockMenuItems);
      setUsingMockData(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageContent = async () => {
    try {
      const content = await getPageContent('menu');
      const map = {};
      (content || []).forEach((entry) => {
        map[entry.section_key] = entry.html || '';
      });
      setPageContent(map);
    } catch (error) {
      console.error('Failed to fetch page content', error);
    }
  };

  const fetchDailySpecials = async () => {
    try {
      const data = await getDailySpecials();
      if (data && data.length > 0) {
        setDailySpecialsMap(buildDailySpecialsMap(data));
      } else {
        setDailySpecialsMap(DEFAULT_DAILY_SPECIALS);
      }
    } catch (error) {
      console.error('Failed to fetch daily specials', error);
      setDailySpecialsMap(DEFAULT_DAILY_SPECIALS);
    }
  };

  // Handle main category change
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setActiveSubCategory(null);
  };

  // Handle sub-category change
  const handleSubCategoryChange = (subCatId) => {
    setActiveSubCategory(subCatId === activeSubCategory ? null : subCatId);
  };

  // Admin functions
  const handleEditItem = (item) => {
    setEditingItem({ ...item });
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;
    try {
      await updateMenuItem(editingItem.id, editingItem);
      await fetchMenuItems(selectedLocation?.slug);
      setEditingItem(null);
      toast({ title: 'Success', description: 'Menu item updated!' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.price) {
      toast({ title: 'Error', description: 'Name and price are required', variant: 'destructive' });
      return;
    }
    try {
      await createMenuItem({
        ...newItem,
        price: parseFloat(newItem.price),
        location_slug: selectedLocation?.slug
      });
      await fetchMenuItems(selectedLocation?.slug);
      setShowAddModal(false);
      setNewItem({
        name: '',
        description: '',
        price: '',
        category: 'starters',
        image: '',
        is_available: true
      });
      toast({ title: 'Success', description: 'Menu item added!' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await deleteMenuItem(itemId);
      await fetchMenuItems(selectedLocation?.slug);
      toast({ title: 'Deleted', description: 'Menu item removed' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAdmin(false);
    setEditMode(false);
    toast({ title: 'Logged Out', description: 'Admin session ended' });
  };

  // Lightbox keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxItem && e.key === 'Escape') {
        setLightboxItem(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxItem]);

  // Deep-link from printed/QR cocktails PDF: /menu?drink=<menu_item_id>
  // After menu items load, switch to the right category, scroll to the card,
  // and briefly highlight it so the patron sees the drink they scanned.
  useEffect(() => {
    if (loading || !menuItems || menuItems.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('drink');
    if (!targetId) return;
    const target = menuItems.find((m) => m.id === targetId);
    if (!target) return;
    // Auto-select the category that contains this item so the card is rendered.
    if (target.category === 'cocktails' || target.category === 'signature-cocktails') {
      setActiveCategory('cocktails');
    } else if (drinksCategoryIds.includes(target.category)) {
      setActiveCategory('drinks');
    } else if (foodCategoryIds.includes(target.category)) {
      setActiveCategory('food');
    } else if (target.category === 'hookah' || target.category === 'hookah-premium') {
      setActiveCategory('hookah');
    } else if (target.category === 'daily-specials') {
      setActiveCategory('daily-specials');
    }
    // Wait for the layout to render, then scroll + highlight.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="menu-card-${targetId}"], [data-testid="menu-line-card-${targetId}"], [data-testid="compact-drink-${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-red-500', 'ring-offset-2', 'ring-offset-slate-900', 'rounded-lg', 'transition-all', 'duration-500');
        setTimeout(() => {
          el.classList.remove('ring-4', 'ring-red-500', 'ring-offset-2', 'ring-offset-slate-900');
        }, 4000);
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, menuItems]);

  const handleImageClick = (item) => {
    if (!editMode) {
      setLightboxItem(item);
    }
  };

  // Group items by category for "All" view
  const itemsByCategory = useMemo(() => {
    const grouped = {};
    const knownCategories = [
      'daily-specials', 'starters', 'sides', 'entrees', 
      'seafood-grits', 'sandwiches', 'salads', 'brunch', 'brunch-sides',
      'beer-wine', 'cocktails', 'signature-cocktails', 'brunch-drinks',
      'mocktails', 'sodas-spritzers', 'teas-lemonades', 'chilled-juices', 'custom-lemonades'
    ];
    
    knownCategories.forEach(cat => {
      grouped[cat] = [];
    });

    menuItems.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });

    return grouped;
  }, [menuItems]);

  // Filter items based on active category and sub-category
  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') {
      return menuItems;
    }
    
    if (activeCategory === 'daily-specials') {
      return menuItems.filter(item => item.category === 'daily-specials');
    }
    
    if (activeCategory === 'food') {
      if (activeSubCategory) {
        return menuItems.filter(item => item.category === activeSubCategory);
      }
      return menuItems.filter(item => foodCategoryIds.includes(item.category));
    }
    
    if (activeCategory === 'drinks') {
      // If a drinks subcategory is selected
      if (activeSubCategory === 'beer-wine') {
        return menuItems.filter(item => beerWineCategoryIds.includes(item.category));
      }
      if (activeSubCategory === 'cocktails') {
        return menuItems.filter(item => cocktailCategoryIds.includes(item.category));
      }
      if (activeSubCategory === 'non-alcoholic') {
        return menuItems.filter(item => nonAlcoholicCategoryIds.includes(item.category));
      }
      // Show all drinks if no subcategory selected
      return menuItems.filter(item => drinksCategoryIds.includes(item.category));
    }
    
    // Legacy support for direct cocktails/non-alcoholic access
    if (activeCategory === 'cocktails') {
      if (activeSubCategory) {
        return menuItems.filter(item => item.category === activeSubCategory);
      }
      return menuItems.filter(item => cocktailCategoryIds.includes(item.category));
    }

    if (activeCategory === 'non-alcoholic') {
      return menuItems.filter(item => nonAlcoholicCategoryIds.includes(item.category));
    }
    
    return menuItems.filter(item => item.category === activeCategory);
  }, [activeCategory, activeSubCategory, menuItems]);

  // Render a section of items with dynamic style support
  const renderSection = (title, items, variant = 'compact', gridCols = 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4', isLineItem = false, categoryId = null) => {
    if (!items || items.length === 0) return null;

    // Drinks sort: Signature Cocktails high→low, all other drinks low→high.
    // display_order is used as a tie-breaker so admins can pin specific items.
    const drinkCategories = ['cocktails', 'signature-cocktails', 'brunch-drinks', 'beer-wine', 'teas-lemonades', 'chilled-juices', 'custom-lemonades', 'non-alcoholic'];
    if (categoryId && drinkCategories.includes(categoryId)) {
      const sortDesc = categoryId === 'cocktails' || categoryId === 'signature-cocktails';
      items = [...items].sort((a, b) => {
        const orderDiff = (a.display_order ?? 999) - (b.display_order ?? 999);
        if (sortDesc && orderDiff !== 0) return orderDiff;
        const pA = a.price || 0;
        const pB = b.price || 0;
        const priceDiff = sortDesc ? pB - pA : pA - pB;
        if (priceDiff !== 0) return priceDiff;
        return orderDiff;
      });
    } else {
      // Non-drinks: keep existing display_order behavior
      items = [...items].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
    }
    
    // Determine style from category settings — DB style OVERRIDES hardcoded params
    const style = categoryId ? (categoryStyles[categoryId] || DEFAULT_CATEGORY_STYLES[categoryId] || 'default') : null;
    
    // 'default' = image cards, 'style_three' = line items, others = MenuStyleRenderer
    let effectiveIsLineItem = isLineItem;
    let effectiveGridCols = gridCols;
    let useNewStyle = false;
    
    if (style) {
      if (style === 'default') {
        effectiveIsLineItem = false;
        effectiveGridCols = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      } else if (style === 'style_three') {
        effectiveIsLineItem = true;
        effectiveGridCols = 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4';
      } else {
        useNewStyle = true;
      }
    }
    
    return (
      <div className="mb-10">
        <h3 className="text-2xl font-bold text-white mb-5 border-b border-slate-700 pb-3">
          {title}
        </h3>
        {useNewStyle ? (
          <MenuStyleRenderer
            items={items}
            category={categoryId}
            categoryStyles={categoryStyles}
            expandedItemId={expandedItemId}
            onToggleExpand={handleToggleExpand}
            onImageClick={handleImageClick}
            editMode={editMode}
            onEdit={handleEditItem}
          />
        ) : (
          <div className={`grid ${effectiveGridCols} ${effectiveIsLineItem ? 'gap-3' : 'gap-5'}`}>
            {items.map((item) => (
              effectiveIsLineItem ? (
                <MenuLineItem key={item.id} item={item} isExpanded={expandedItemId === item.id} onToggleExpand={handleToggleExpand} />
              ) : (
                <MenuCard 
                  key={item.id} 
                  item={item} 
                  variant={variant} 
                  editMode={editMode} 
                  onEdit={handleEditItem} 
                  onImageClick={handleImageClick} 
                  isExpanded={expandedItemId === item.id}
                  onToggleExpand={handleToggleExpand}
                />
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render compact 3-column drink items (for beers, wines with short descriptions)
  const renderCompactDrinkSection = (title, items) => {
    if (!items || items.length === 0) return null;

    // Sort by price ASC (low → high), then display_order as tie-breaker
    items = [...items].sort((a, b) => {
      const pA = a.price || 0;
      const pB = b.price || 0;
      if (pA !== pB) return pA - pB;
      return (a.display_order ?? 999) - (b.display_order ?? 999);
    });
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-bold text-white mb-4 border-b border-slate-700 pb-2">
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((item) => (
            <div 
              key={item.id} 
              className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-all duration-200 border border-slate-700/30"
              data-testid={`compact-drink-${item.id}`}
            >
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-white truncate">{item.name}</h4>
                {item.description && item.description.length > 0 && (
                  <p className="text-slate-500 text-xs truncate">{item.description}</p>
                )}
              </div>
              <div className="ml-3 flex-shrink-0">
                {item.variations && item.variations.length > 0 ? (
                  <div className="text-right">
                    {item.variations.map((v, vi) => (
                      <div key={`${v.name}-${v.price}`} className="flex items-center gap-1.5">
                        <span className="text-slate-500 text-[10px]">{v.name}</span>
                        <span className="text-red-500 font-bold text-sm">${v.price}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-red-500 font-bold text-sm">
                    {item.price > 0 ? `$${item.price}` : 'MKT'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Categorize beer & wine items by type
  const categorizeBeerWineItems = (items) => {
    if (!items) return {};
    
    const beerNames = ['Angry Orchard', 'Blue Moon', 'Bud Light', 'Corona', 'Guinness', 'Heineken', 'Mich Ultra', 'Modelo', 'Modelo Negro', 'Stella Artois', 'Yuengling'];
    const champagneNames = ['Belaire', 'House Champagne', 'Moet'];
    const energyNames = ['Red Bull'];
    
    const categories = {
      beer: [],
      champagne: [],
      redWine: [],
      whiteWine: [],
      rose: [],
      sparkling: [],
      energy: [],
      juice: [],
      water: [],
      other: []
    };
    
    items.forEach(item => {
      const name = item.name.toLowerCase();
      const desc = (item.description || '').toLowerCase();
      const sub = (item.subcategory || '').toLowerCase();
      
      // Use subcategory field first if present
      if (sub === 'red wine') { categories.redWine.push(item); return; }
      if (sub === 'white wine') { categories.whiteWine.push(item); return; }
      if (sub === 'rosé' || sub === 'rose') { categories.rose.push(item); return; }
      if (sub === 'sparkling') { categories.sparkling.push(item); return; }

      if (beerNames.some(b => name.includes(b.toLowerCase())) || (name === 'stella' && !name.includes('rosa'))) {
        categories.beer.push(item);
      } else if (champagneNames.some(c => name.includes(c.toLowerCase()))) {
        categories.champagne.push(item);
      } else if (name.includes('cabernet') || name.includes('merlot') || name.includes('pinot noir') || name.includes('malbec') || desc.includes('red wine')) {
        categories.redWine.push(item);
      } else if (name.includes('chardonnay') || name.includes('sauvignon blanc') || name.includes('pinot grigio') || name.includes('moscato') || name.includes('riesling') || desc.includes('white wine')) {
        categories.whiteWine.push(item);
      } else if (name.includes('rosé') || name.includes('rose wine') || desc.includes('rosé')) {
        categories.rose.push(item);
      } else if (name.includes('brut') || name.includes('prosecco') || desc.includes('sparkling')) {
        categories.sparkling.push(item);
      } else if (energyNames.some(e => name.includes(e.toLowerCase())) || desc.includes('energy')) {
        categories.energy.push(item);
      } else if (name.includes('juice') || desc.includes('juice')) {
        categories.juice.push(item);
      } else if (name.includes('water')) {
        categories.water.push(item);
      } else {
        categories.other.push(item);
      }
    });
    
    return categories;
  };

  // Render all beer & wine sections by type
  const renderBeerWineSections = () => {
    const categories = categorizeBeerWineItems(itemsByCategory['beer-wine']);
    
    return (
      <div className="space-y-6">
        {renderCompactDrinkSection('🍺 Beer', categories.beer)}
        {renderCompactDrinkSection('🍾 Champagne', categories.champagne)}
        {renderCompactDrinkSection('🍷 Red Wine', categories.redWine)}
        {renderCompactDrinkSection('🥂 White Wine', categories.whiteWine)}
        {renderCompactDrinkSection('✨ Sparkling', categories.sparkling)}
        {renderCompactDrinkSection('⚡ Energy Drinks', categories.energy)}
        {renderCompactDrinkSection('💧 Water', categories.water)}
        {categories.other.length > 0 && renderCompactDrinkSection('Other', categories.other)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-red-500 animate-spin mx-auto mb-4" />
          <p className="text-white">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Admin Bar */}
      {isAdmin && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white py-2 px-4 z-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Admin Mode - Menu Editor</span>
          </div>
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddModal(true)}
                  className="bg-green-600 hover:bg-green-700 h-8"
                  data-testid="add-menu-item-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Item
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setEditMode(false)}
                  className="border-white text-white hover:bg-white/20 h-8"
                >
                  Done Editing
                </Button>
              </>
            ) : (
              <Button 
                size="sm" 
                onClick={() => setEditMode(true)}
                className="bg-white text-red-600 hover:bg-gray-100 h-8"
                data-testid="edit-menu-btn"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Edit Menu
              </Button>
            )}
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20 h-8"
            >
              Dashboard
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleAdminLogout}
              className="text-white hover:bg-white/20 h-8"
            >
              <LogOut className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-slate-900 border-red-600/50 w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Edit Menu Item</h2>
                <Button variant="ghost" size="sm" onClick={() => setEditingItem(null)} className="text-slate-400">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Name</label>
                  <Input
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Description</label>
                  <Textarea
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300 mb-1 block">Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingItem.price}
                      onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 mb-1 block">Category</label>
                    <select
                      value={editingItem.category}
                      onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      {Object.entries(categoryNames).map(([key, name]) => (
                        <option key={key} value={key}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Image</label>
                  <ImageUploader 
                    currentImage={editingItem.image}
                    onImageUpload={(url) => setEditingItem({ ...editingItem, image: url })}
                  />
                  <div className="mt-2">
                    <label className="text-xs text-slate-400">Or enter URL directly:</label>
                    <Input
                      value={editingItem.image || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, image: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSaveItem} className="flex-1 bg-green-600 hover:bg-green-700">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button 
                    onClick={() => handleDeleteItem(editingItem.id)} 
                    variant="outline" 
                    className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-slate-900 border-green-600/50 w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Add Menu Item</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)} className="text-slate-400">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Name *</label>
                  <Input
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Dish name"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Description</label>
                  <Textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Brief description..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300 mb-1 block">Price ($) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newItem.price}
                      onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="12.99"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 mb-1 block">Category</label>
                    <select
                      value={newItem.category}
                      onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      {Object.entries(categoryNames).map(([key, name]) => (
                        <option key={key} value={key}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Image</label>
                  <ImageUploader 
                    currentImage={newItem.image}
                    onImageUpload={(url) => setNewItem({ ...newItem, image: url })}
                  />
                  <div className="mt-2">
                    <label className="text-xs text-slate-400">Or enter URL directly:</label>
                    <Input
                      value={newItem.image || ''}
                      onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white mt-1"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <Button onClick={handleAddItem} className="w-full bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Menu
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxItem && (
        <ImageLightbox 
          image={lightboxItem}
          onClose={() => setLightboxItem(null)}
        />
      )}

      {/* Header with logo and location */}
      <div className={`container mx-auto px-4 pt-8 pb-4 ${isAdmin ? 'mt-12' : ''}`}>
        <div className="flex flex-col items-center gap-4">
          {/* Logo */}
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_57379523-4651-4150-aa1e-60b8df6a4f7c/artifacts/zzljit87_Untitled%20design.png" 
              alt="Fin & Feathers Restaurants"
              className="max-h-32 md:max-h-40 w-auto mx-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="bg-slate-800/70 border-red-600/50 text-red-500 hover:bg-slate-700 hover:text-red-400 hover:border-red-500 transition-all duration-300 px-6 py-2.5 rounded-lg"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
            
            <Button
              onClick={() => navigate('/locations')}
              variant="outline"
              className="bg-slate-800/70 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white transition-all duration-300 px-6 py-2.5 rounded-lg"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Locations
            </Button>
          </div>

          {/* Location Picker */}
          {selectedLocation && (
            <div className="flex items-center gap-2 bg-slate-800/70 rounded-lg px-4 py-2 border border-slate-700/50" data-testid="menu-location-picker">
              <MapPin className="w-4 h-4 text-red-500 shrink-0" />
              <select
                value={selectedLocation.slug}
                onChange={(e) => {
                  const loc = locations.find(l => l.slug === e.target.value);
                  if (loc) {
                    setSelectedLocation(loc);
                    localStorage.setItem('ff_user_location', loc.slug);
                  }
                }}
                className="bg-transparent text-white text-sm font-medium border-none outline-none cursor-pointer appearance-none pr-4"
                data-testid="menu-location-select"
              >
                {locations.map(loc => (
                  <option key={loc.slug} value={loc.slug} className="bg-slate-800 text-white">
                    {loc.name?.replace('Fin & Feathers - ', '')}
                  </option>
                ))}
              </select>
            </div>
          )}
          {findingLocation && (
            <p className="text-slate-400 text-xs animate-pulse">Detecting your location...</p>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
          Our Menu
        </h2>
        <div
          className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
          data-testid="page-content-menu-hero"
          {...safeHtml(heroHtml)}
        />
      </div>

      {/* Signature Selections Header */}
      <div className="container mx-auto px-4 mb-8">
        <div className="flex items-center gap-3">
          <span className="text-red-500 text-2xl">✨</span>
          <h2 className="text-3xl font-bold text-white">Signature Selections</h2>
        </div>
      </div>

      {/* Main Category Filter - 4 buttons */}
      <div className="container mx-auto px-4 mb-4">
        <div className="flex flex-wrap gap-3 justify-center items-center">
          {mainCategories
            .filter((cat) => cat.id !== 'daily-specials' || specialActiveNow)
            .map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`px-5 py-2.5 rounded-full font-medium transition-all duration-200 flex items-center gap-2 ${
                activeCategory === cat.id
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}

          {/* Upcoming special countdown chip — only when no special is running */}
          {nextSpecialChip && (
            <div
              className="px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/10 to-red-500/10 border border-amber-500/40 text-amber-300 text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 shadow-inner shadow-amber-500/5"
              data-testid="next-special-chip"
              title={`Upcoming: ${nextSpecialChip.label}`}
            >
              <span className="text-sm" aria-hidden="true">{nextSpecialChip.emoji}</span>
              <span className="hidden sm:inline">{nextSpecialChip.label}</span>
              <span className="sm:hidden">{nextSpecialChip.label.replace(' • ', ' ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Sub-Category Filter for Food */}
      {activeCategory === 'food' && (
        <div className="container mx-auto px-4 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {foodSubCategories.map((subCat) => (
              <button
                key={subCat.id}
                onClick={() => handleSubCategoryChange(subCat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  activeSubCategory === subCat.id
                    ? 'bg-red-500/80 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                }`}
                data-testid={`subcategory-${subCat.id}`}
              >
                <span>{subCat.icon}</span>
                <span>{subCat.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-Category Filter for Drinks */}
      {activeCategory === 'drinks' && (
        <div className="container mx-auto px-4 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {drinksSubCategories.map((subCat) => (
              <button
                key={subCat.id}
                onClick={() => handleSubCategoryChange(subCat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  activeSubCategory === subCat.id
                    ? 'bg-red-500/80 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                }`}
                data-testid={`subcategory-${subCat.id}`}
              >
                <span>{subCat.icon}</span>
                <span>{subCat.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Cocktails Sections Header Only */}
      {activeCategory === 'cocktails' && (
        <div className="container mx-auto px-4 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="text-slate-400 text-sm">$5 Specials • Signature • Brunch Drinks</span>
          </div>
        </div>
      )}

      {/* Menu Grid */}
      <div className="container mx-auto px-4 pb-16">
        
        {/* House Rules - At the top */}
        <div className="mb-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl space-y-2">
          <p className="text-red-400 text-lg text-center font-bold">House Rules</p>
          <p className="text-red-300/90 text-sm text-center">
            <span className="font-semibold">Age Requirement:</span> We are a 21+ establishment. Please ensure all guests have a valid government-issued ID ready upon arrival.
          </p>
          <p className="text-red-300/90 text-sm text-center">
            <span className="font-semibold">Service Policy:</span> To support our dedicated team, a 20% service charge is added to all checks.
          </p>
          <p className="text-red-300/90 text-sm text-center" data-testid="house-rules-togo-surcharge">
            <span className="font-semibold">To-Go Orders:</span> A $2.00 surcharge applies to each to-go item.
          </p>
        </div>

        {/* ALL VIEW - Show everything organized with drinks at the bottom */}
        {activeCategory === 'all' && (
          <div className="space-y-10">
            {/* $5 Daily Specials — hidden when outside today's special hours */}
            {specialActiveNow && itemsByCategory['daily-specials']?.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-white mb-2 border-b border-slate-700 pb-3">
                  $5 Daily Specials
                </h3>
                <p className="text-slate-400 text-sm mb-2">MON-FRI 12PM-8PM • SATURDAY 5PM-8PM • SUNDAY 6PM-CLOSE</p>
                <p className="text-amber-400 text-xs mb-4 italic" data-testid="daily-specials-togo-surcharge-all">Please note: a $2.00 surcharge applies to all to-go orders.</p>
                
                {/* Today's Special Highlight */}
                <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded-xl p-4 mb-6" data-testid="todays-special-highlight">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl" data-testid="todays-special-emoji">{todaysSpecial.emoji}</span>
                    <span className="text-red-400 font-semibold text-sm uppercase tracking-wide" data-testid="todays-special-day">Today's Special • {todayName}</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-1" data-testid="todays-special-title">{todaysSpecial.name}</h4>
                  <p className="text-slate-300 text-sm mb-2" data-testid="todays-special-description">{todaysSpecial.description}</p>
                  <p className="text-red-400 text-xs font-medium" data-testid="todays-special-hours">{todaysSpecial.hours}</p>
                </div>
                
                {/* Food Items */}
                {itemsByCategory['daily-specials'].filter(item => item.type === 'food').length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xl font-semibold text-slate-300 mb-4">Food</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">
                      {itemsByCategory['daily-specials']
                        .filter(item => item.type === 'food')
                        .map((item) => (
                          <MenuCard key={item.id} item={item} variant="compact" editMode={editMode} onEdit={handleEditItem} onImageClick={handleImageClick} isExpanded={expandedItemId === item.id} onToggleExpand={handleToggleExpand} />
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Drink Items within Daily Specials */}
                {itemsByCategory['daily-specials'].filter(item => item.type === 'drink').length > 0 && (
                  <div>
                    <h4 className="text-xl font-semibold text-slate-300 mb-4">Drinks</h4>
                    <div className="space-y-3">
                      {itemsByCategory['daily-specials']
                        .filter(item => item.type === 'drink')
                        .map((item) => (
                          <MenuLineItem key={item.id} item={item} isExpanded={expandedItemId === item.id} onToggleExpand={handleToggleExpand} />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* FOOD SECTIONS */}
            {renderSection('Starters', itemsByCategory['starters'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'starters')}
            {renderSection('Sides', itemsByCategory['sides'], 'compact', 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4', true, 'sides')}
            {renderSection('Entrees', itemsByCategory['entrees'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'entrees')}
            {renderSection('Seafood & Grits', itemsByCategory['seafood-grits'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'seafood-grits')}
            {renderSection('Sandwiches', (itemsByCategory['sandwiches'] || []).filter(item => !item.name.toLowerCase().startsWith('add ')), 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'sandwiches')}
            {/* Sandwich Add-Ons as single centered line */}
            {(itemsByCategory['sandwiches'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).length > 0 && (
              <div className="-mt-6 mb-10 px-1">
                <p className="text-slate-300 text-sm">
                  {(itemsByCategory['sandwiches'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).sort((a, b) => (b.price || 0) - (a.price || 0)).map((item, idx, arr) => (
                    <span key={item.id}>
                      <span>{item.name.replace('Add ', '').replace(' to Any Sandwich', '')}</span>{' '}
                      <span className="text-amber-400 font-bold">{item.price > 0 ? `$${item.price}` : 'MKT'}</span>
                      {idx < arr.length - 1 && <span className="text-slate-600 mx-2">|</span>}
                    </span>
                  ))}
                </p>
              </div>
            )}
            {renderSection('Salads', (itemsByCategory['salads'] || []).filter(item => !item.name.toLowerCase().startsWith('add ')), 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'salads')}
            {/* Protein Add-Ons as single centered line */}
            {(itemsByCategory['salads'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).length > 0 && (
              <div className="-mt-6 mb-10 px-1">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Add Protein <span className="normal-case">(for an additional charge)</span></p>
                <p className="text-slate-300 text-sm">
                  {(itemsByCategory['salads'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99)).map((item, idx, arr) => (
                    <span key={item.id}>
                      <span>{item.name.replace('Add ', '')}</span>{' '}
                      <span className="text-amber-400 font-bold">{item.price > 0 ? `$${item.price}` : 'MKT'}</span>
                      {idx < arr.length - 1 && <span className="text-slate-600 mx-2">|</span>}
                    </span>
                  ))}
                </p>
              </div>
            )}
            {renderSection('Brunch', itemsByCategory['brunch'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'brunch')}
            {renderSection('Brunch Sides', itemsByCategory['brunch-sides'], 'compact', 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4', true, 'brunch-sides')}

            {/* Allergy Warning */}
            <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl">
              <p className="text-red-400 text-sm text-center">
                Allergens are marked with an *. Some food items may contain raw or undercooked ingredients and/or dairy. Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs could heighten the risk of foodborne illness, particularly for individuals with specific medical conditions. If you have food allergies or dietary restrictions, kindly notify one of our team members prior to ordering.
              </p>
            </div>

            {/* DRINKS AT THE BOTTOM */}
            <div className="pt-8 border-t border-slate-700">
              <div className="flex items-center gap-3 mb-8">
                <span className="text-2xl">🍹</span>
                <h2 className="text-3xl font-bold text-white">Cocktails & Drinks</h2>
              </div>
              
              {renderSection('Signature Cocktails', [...(itemsByCategory['cocktails'] || []), ...(itemsByCategory['signature-cocktails'] || [])], 'compact', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'cocktails')}
              {renderBeerWineSections()}
              {renderSection('Brunch Drinks', itemsByCategory['brunch-drinks'], 'compact', 'grid-cols-1', false, 'brunch-drinks')}
              {renderSection('Handcrafted Mocktails ($7)', itemsByCategory['mocktails'], 'compact', 'grid-cols-1', false, 'mocktails')}
              {renderClassicRefreshments()}
              {renderSection('Custom Fruit Lemonades (No Refill)', itemsByCategory['custom-lemonades'], 'compact', 'grid-cols-1', true, 'custom-lemonades')}
            </div>
          </div>
        )}

        {/* DAILY SPECIALS VIEW — only when the special is currently active */}
        {activeCategory === 'daily-specials' && specialActiveNow && (
          <div>
            <p className="text-slate-400 text-sm mb-2">MON-FRI 12PM-8PM • SATURDAY 5PM-8PM • SUNDAY 6PM-CLOSE</p>
            <p className="text-amber-400 text-xs mb-4 italic" data-testid="daily-specials-togo-surcharge">Please note: a $2.00 surcharge applies to all to-go orders.</p>
            
            {/* Today's Special Highlight */}
            <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded-xl p-4 mb-6" data-testid="todays-special-highlight">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl" data-testid="todays-special-emoji">{todaysSpecial.emoji}</span>
                <span className="text-red-400 font-semibold text-sm uppercase tracking-wide" data-testid="todays-special-day">Today's Special • {todayName}</span>
              </div>
              <h4 className="text-xl font-bold text-white mb-1" data-testid="todays-special-title">{todaysSpecial.name}</h4>
              <p className="text-slate-300 text-sm mb-2" data-testid="todays-special-description">{todaysSpecial.description}</p>
              <p className="text-red-400 text-xs font-medium" data-testid="todays-special-hours">{todaysSpecial.hours}</p>
            </div>
            
            {/* Food section */}
            {filteredItems.filter(item => item.type === 'food').length > 0 && (
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-4">Food</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {filteredItems
                    .filter(item => item.type === 'food')
                    .map((item) => (
                      <MenuCard key={item.id} item={item} variant="compact" editMode={editMode} onEdit={handleEditItem} onImageClick={handleImageClick} />
                    ))}
                </div>
              </div>
            )}
            
            {/* Drinks section */}
            {filteredItems.filter(item => item.type === 'drink').length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Drinks</h3>
                <div className="space-y-3">
                  {filteredItems
                    .filter(item => item.type === 'drink')
                    .map((item) => (
                      <MenuLineItem key={item.id} item={item} isExpanded={expandedItemId === item.id} onToggleExpand={handleToggleExpand} />
                    ))}
                </div>
              </div>
            )}

            {/* Allergy Warning */}
            <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl">
              <p className="text-red-400 text-sm text-center">
                Allergens are marked with an *. Some food items may contain raw or undercooked ingredients and/or dairy. Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs could heighten the risk of foodborne illness, particularly for individuals with specific medical conditions. If you have food allergies or dietary restrictions, kindly notify one of our team members prior to ordering.
              </p>
            </div>
          </div>
        )}

        {/* FOOD VIEW */}
        {activeCategory === 'food' && (
          <div className="space-y-10">
            {activeSubCategory ? (
              // Show only selected sub-category
              <>
                {activeSubCategory === 'sides' || activeSubCategory === 'brunch-sides' ? (
                  renderSection(categoryNames[activeSubCategory], filteredItems, 'compact', 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4', true, activeSubCategory)
                ) : (
                  // All other food categories show as cards with images
                  renderSection(categoryNames[activeSubCategory], filteredItems, 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, activeSubCategory)
                )}
              </>
            ) : (
              // Show all food categories
              <>
                {renderSection('Starters', itemsByCategory['starters'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'starters')}
                {renderSection('Sides', itemsByCategory['sides'], 'compact', 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4', true, 'sides')}
                {renderSection('Entrees', itemsByCategory['entrees'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'entrees')}
                {renderSection('Seafood & Grits', itemsByCategory['seafood-grits'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'seafood-grits')}
                {renderSection('Sandwiches', (itemsByCategory['sandwiches'] || []).filter(item => !item.name.toLowerCase().startsWith('add ')), 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'sandwiches')}
                {/* Sandwich Add-Ons as line items */}
                {(itemsByCategory['sandwiches'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).length > 0 && (
                  <div className="-mt-6 mb-10 px-1">
                    <div className="space-y-1.5">
                      {(itemsByCategory['sandwiches'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).sort((a, b) => (b.price || 0) - (a.price || 0)).map((item) => (
                        <div key={item.id} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                          <span className="text-slate-400 text-sm">{item.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="flex-1 border-b border-dotted border-slate-700 w-8" />
                            <span className="text-amber-400 font-bold text-sm">${item.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {renderSection('Salads', (itemsByCategory['salads'] || []).filter(item => !item.name.toLowerCase().startsWith('add ')), 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'salads')}
                {/* Protein Add-Ons as line items */}
                {(itemsByCategory['salads'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).length > 0 && (
                  <div className="-mt-6 mb-10 px-1">
                    <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Add Protein</p>
                    <div className="space-y-1.5">
                      {(itemsByCategory['salads'] || []).filter(item => item.name.toLowerCase().startsWith('add ')).map((item) => (
                        <div key={item.id} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                          <span className="text-slate-400 text-sm">{item.name.replace('Add ', '')}</span>
                          <div className="flex items-center gap-2">
                            <span className="flex-1 border-b border-dotted border-slate-700 w-8" />
                            <span className="text-amber-400 font-bold text-sm">+${item.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {renderSection('Brunch', itemsByCategory['brunch'], 'default', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'brunch')}
                {renderSection('Brunch Sides', itemsByCategory['brunch-sides'], 'compact', 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4', true, 'brunch-sides')}
              </>
            )}
            
            {/* Allergy Warning */}
            <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl">
              <p className="text-red-400 text-sm text-center">
                Allergens are marked with an *. Some food items may contain raw or undercooked ingredients and/or dairy. Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs could heighten the risk of foodborne illness, particularly for individuals with specific medical conditions. If you have food allergies or dietary restrictions, kindly notify one of our team members prior to ordering.
              </p>
            </div>
          </div>
        )}

        {/* COCKTAILS VIEW */}
        {activeCategory === 'cocktails' && (
          <div className="space-y-10">
            {renderSection('Signature Cocktails', [...(itemsByCategory['cocktails'] || []), ...(itemsByCategory['signature-cocktails'] || [])], 'compact', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'cocktails')}
            {renderSection('Brunch Drinks', itemsByCategory['brunch-drinks'], 'compact', 'grid-cols-1', true)}
            {renderSection('Handcrafted Mocktails ($7)', itemsByCategory['mocktails'], 'compact', 'grid-cols-1', true)}
            {renderClassicRefreshments()}
            {renderSection('Custom Fruit Lemonades (No Refill)', itemsByCategory['custom-lemonades'], 'compact', 'grid-cols-1', true)}
            {/* Responsible Drinking & Takeout Notice */}
            <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl space-y-2" data-testid="drinks-policy-notice">
              <p className="text-red-400 text-sm text-center font-medium">
                Please drink responsibly—never drink and drive. We encourage the use of rideshare services for a safe trip home. We reserve the right to refuse service to any guest who appears intoxicated.
              </p>
              <p className="text-amber-300 text-xs text-center" data-testid="takeout-beverages-policy">
                <span className="font-semibold">Beverages:</span> We do not offer takeout alcoholic beverages. To-go drinks are limited to bottled water, Red Bull, and canned (unopened) Ginger Beer.
              </p>
            </div>
          </div>
        )}

        {/* NON-ALCOHOLIC VIEW */}
        {activeCategory === 'non-alcoholic' && (
          <div className="space-y-10">
            {renderSection('Handcrafted Mocktails ($7)', itemsByCategory['mocktails'], 'compact', 'grid-cols-1', true)}
            {renderClassicRefreshments()}
            {renderSection('Custom Fruit Lemonades (No Refill)', itemsByCategory['custom-lemonades'], 'compact', 'grid-cols-1', true)}
          </div>
        )}

        {/* DRINKS VIEW - Main drinks category with subcategories */}
        {activeCategory === 'drinks' && (
          <div className="space-y-10">
            {/* Beer & Wine selected */}
            {activeSubCategory === 'beer-wine' && (
              <>
                {renderBeerWineSections()}
                {/* Responsible Drinking & Takeout Notice */}
                <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl space-y-2">
                  <p className="text-red-400 text-sm text-center font-medium">
                    Please drink responsibly—never drink and drive. We encourage the use of rideshare services for a safe trip home. We reserve the right to refuse service to any guest who appears intoxicated.
                  </p>
                  <p className="text-amber-300 text-xs text-center">
                    <span className="font-semibold">Beverages:</span> We do not offer takeout alcoholic beverages. To-go drinks are limited to bottled water, Red Bull, and canned (unopened) Ginger Beer.
                  </p>
                </div>
              </>
            )}
            
            {/* Cocktails selected */}
            {activeSubCategory === 'cocktails' && (
              <>
                {renderSection('Signature Cocktails', [...(itemsByCategory['cocktails'] || []), ...(itemsByCategory['signature-cocktails'] || [])], 'compact', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'cocktails')}
                {renderSection('Brunch Drinks', itemsByCategory['brunch-drinks'], 'compact', 'grid-cols-1', true)}
                {/* Responsible Drinking & Takeout Notice */}
                <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl space-y-2">
                  <p className="text-red-400 text-sm text-center font-medium">
                    Please drink responsibly—never drink and drive. We encourage the use of rideshare services for a safe trip home. We reserve the right to refuse service to any guest who appears intoxicated.
                  </p>
                  <p className="text-amber-300 text-xs text-center">
                    <span className="font-semibold">Beverages:</span> We do not offer takeout alcoholic beverages. To-go drinks are limited to bottled water, Red Bull, and canned (unopened) Ginger Beer.
                  </p>
                </div>
              </>
            )}
            
            {/* Non-Alcoholic selected */}
            {activeSubCategory === 'non-alcoholic' && (
              <>
                {renderSection('Handcrafted Mocktails ($7)', itemsByCategory['mocktails'], 'compact', 'grid-cols-1', true)}
                {renderClassicRefreshments()}
                {renderSection('Custom Fruit Lemonades (No Refill)', itemsByCategory['custom-lemonades'], 'compact', 'grid-cols-1', true)}
              </>
            )}
            
            {/* No subcategory - show all drinks */}
            {!activeSubCategory && (
              <>
                {renderSection('Signature Cocktails', [...(itemsByCategory['cocktails'] || []), ...(itemsByCategory['signature-cocktails'] || [])], 'compact', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3', false, 'cocktails')}
                {renderBeerWineSections()}
                {renderSection('Brunch Drinks', itemsByCategory['brunch-drinks'], 'compact', 'grid-cols-1', true)}
                {renderSection('Handcrafted Mocktails ($7)', itemsByCategory['mocktails'], 'compact', 'grid-cols-1', true)}
                {renderClassicRefreshments()}
                {renderSection('Custom Fruit Lemonades (No Refill)', itemsByCategory['custom-lemonades'], 'compact', 'grid-cols-1', true)}
                {/* Responsible Drinking & Takeout Notice */}
                <div className="mt-8 p-4 bg-red-900/20 border border-red-600/50 rounded-xl space-y-2">
                  <p className="text-red-400 text-sm text-center font-medium">
                    Please drink responsibly—never drink and drive. We encourage the use of rideshare services for a safe trip home. We reserve the right to refuse service to any guest who appears intoxicated.
                  </p>
                  <p className="text-amber-300 text-xs text-center">
                    <span className="font-semibold">Beverages:</span> We do not offer takeout alcoholic beverages. To-go drinks are limited to bottled water, Red Bull, and canned (unopened) Ginger Beer.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* HOOKAH VIEW */}
        {activeCategory === 'hookah' && (
          <div className="space-y-10">
            <HookahSection locationSlug={selectedLocation?.slug} />
          </div>
        )}

        {/* Empty state */}
        {filteredItems.length === 0 && activeCategory !== 'hookah' && (
          <div className="text-center py-12">
            <p className="text-slate-400 text-lg">No items in this category yet.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default MenuPage;
